import { useTableController } from "@/components/datatable/controller"
import { DataTable } from "@/components/datatable/datatable"
import { DataTableSupabase } from "@/components/datatable/supadatatable"
import { ToolbarItem } from "@/components/datatable/toolbaritem"
import { Page, PageTitle } from "@/components/page"
import { useDatabaseData, usePage } from "@/components/page/hooks"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Progress } from "@/components/ui/progress"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Textarea } from "@/components/ui/textarea"
import { ToMarkdown } from "@/components/ui/tomarkdown"
import { app } from "@/lib/app"
import { supabase } from "@/lib/data"
import { documents } from '@/lib/agents/search/documents'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { BookCopy, FileText, Globe, GlobeLock, Plus, Puzzle, Trash2, X } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { extractor } from "./extractor"
import { chunkText, createEmbeddings } from "./utils"
import { estimateTokenCount } from "tokenx"
import { Statuses } from "./status"


export const supa = {
  title: "Supabase",
  description: "Manage your organization's Documents.",
  route: "/docu/supa",
  url: "/docu/supa",
  icon: BookCopy,
  component,
  group: "docs",
  resource: "collections",
  action: "read",
} satisfies PageConfig


const acceptedFileTypes = Object.keys(documents.supportedExtensions).map(ext => `.${ext}`).join(",")

const columns: Columns<Table<"vectors"> & { distance?: number }> = {
  id: { header: "id", size: 72, cell: DataTable.cellRender.number },
  name: { header: "Name", size: 450, cell: ({ row }) => `${row.original.name}` },
  // locale: { header: "Domain", size: 100 },
  reference: { header: "Domain", size: 100, cell: ({ row }) => row.original.reference?.["name"] },
  isChunk: { header: "Is Chunk", size: 130, cell: ({ row }) => row.original.isChunk ? "Yes" : "No" },
  created_at: { header: "Created", cell: DataTable.cellRender.date, size: 166 },
  distance: { header: "Distance", cell: DataTable.cellRender.number, size: 120 },
  team: { header: "Team", size: 180, cell: ({ row }) => row.original.team?.["name"] },
  tokens: { header: "Estimated Tokens", size: 180, cell: DataTable.cellRender.number },
}

type Vector = Table<"vectors">

interface FileToAdd {
  file: File
  url?: string
  name?: string
  error?: string
  progress?: number
  status?: 'pending' | 'uploading' | 'completed' | 'error'
}

function component() {


  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const uploading = useRef(false)
  const [showChunks, setShowChunks] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<FileToAdd[]>([])
  const [currentFileIndex, setCurrentFileIndex] = useState(0)
  const [selectedItems, setSelectedItems] = useState<Vector[]>([])
  const [showSummary, setShowSummary] = useState(false)
  const [bulkDomainConfirm, setBulkDomainConfirm] = useState<{ open: boolean; domainId: number | null; domainName: string }>({ open: false, domainId: null, domainName: "" })
  const [uploadSummary, setUploadSummary] = useState<{ successful: number; errors: { file: string; error: string }[] }>({ successful: 0, errors: [] })
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isNewDocDialogOpen, setIsNewDocDialogOpen] = useState(false)
  const [newDocName, setNewDocName] = useState("")
  const [newDocContent, setNewDocContent] = useState("")
  const [isCreatingDoc, setIsCreatingDoc] = useState(false)
  const ctrl = useTableController()
  const [preview, setPreview] = useState<Vector | null>(null)
  const { team, navigate } = usePage()
  const keys = useRef("")
  const search = useRef({
    query: "",
    similarity: false
  })

  const { data: domains, loading: domainsLoading } = useDatabaseData("domains", { filter: q => q.eq("team", team?.id) })





  const filter = useCallback((q: any) => {
    return showChunks ? q.eq("team", team?.id) : q.eq("isChunk", false).eq("team", team?.id)
  }, [showChunks, team?.id])


  function onItemSelectionChange(selectedIds: Vector[]) {
    setSelectedItems(selectedIds)
  }

  async function handleSearch({ query, similarity }: { query: string; similarity: boolean }) {
    console.log("Search triggered:", { query, similarity })
    search.current.query = query
    search.current.similarity = similarity
    ctrl.reload()
  }

  async function onExternalLoad() {

    if (uploading.current) return

    const { query, similarity } = search.current

    if (!query) return null

    if (similarity) {

      const keys = await app.fetchAPIkeys(team?.id)
      const embedding = await documents.createEmbedding(query, keys.openai)

      let { data, error } = await supabase.rpc('match_vectors', {
        query_embedding: embedding as any,      // number[]
        match_count: 100,
        // filter_is_chunk: false,           // or true/false
        // filter_locale: null,             // 'en', etc.
        // filter_status: null,             // e.g., 200
        // filter_ids: [1, 2, 3],           // int[] or null
        // origin_lon: null,                // number or null
        // origin_lat: null,                // number or null
        // max_distance_meters: null,       // number or null
        // bbox_min_lon: null,              // number or null
        // bbox_min_lat: null,              // number or null
        // bbox_max_lon: null,              // number or null
        // bbox_max_lat: null,              // number or null
        // similarity: 'l2',                // or 'cosine'
        // similarity: 'cosine',            // or 'cosine'
      })

      data = data || []
      console.log(data)
      data = data.filter((item: any) => item.team === team?.id)

      return { data, error }
    }

    const { data, error } = await supabase.from('vectors')
      .select('id,created_at,name,isChunk,locale,url,content').eq('team', team?.id)
      .or(`name.ilike.%${query.trim()}%, content.ilike.%${query.trim()}%, content.ilike.%example.com%`)

    return { data, error }



  }

  function handleClear() {
    console.log("Search cleared")
    search.current.query = ""
    search.current.similarity = false
    ctrl.reload()
  }

  async function onDeleteSelected() {
    const selected = ctrl.getSeleted()
    console.log(selected)

    if (selected.length === 0) return
    ctrl.setLoading(true)
    await supabase.from("vectors").delete().in("id", selected.map((item: any) => item.id))
    ctrl.setLoading(false)
    ctrl.reload()
  }

  async function onFileUpload(file: FileToAdd) {

    try {

      console.log(`Uploading file... ${file.name}`)

      const extension = file.name.split('.').pop()

      if (!documents.supportedExtensions[extension]) {
        console.log("Unsupported file type:", extension)
        file.error = "Unsupported file type"
        return
      }

      const { data: existingFiles } = await supabase.from('vectors').select('size, name').eq('size', file.file.size).eq('team', team.id)
      if (existingFiles && existingFiles.length > 0) {
        const sameNameFile = existingFiles.find(ef => (ef.name?.toLowerCase() ?? "") == file.name.toLowerCase())
        if (sameNameFile) {
          file.error = "Duplicated file, Skipped."
          console.log(`File with same name exists. Skipping`)
          return
        }
      }

      let ext: ExtractedDocument = {
        locale: "",
        text: "",
        chunks: [],
      }

      try {

        if (extension === "pdf") {
          ext = await extractor.extractPdf(file.file, keys.current)
        } else if (extension === "docx" || extension === "doc") {
          ext = await extractor.extractDoc(file.file, keys.current)
        } else if (extension === "xlsx" || extension === "csv" || extension === "xls") {
          ext = await extractor.extractExcel(file.file, keys.current)
        } else if (extension === "txt" || extension === "md" || extension === "json" || extension === "xml") {
          ext = await extractor.convertTextToMarkdown({ buffer: await file.file.arrayBuffer(), extension: extension as any, })
        } else if (extension === "jpg" || extension === "png" || extension === "webp" || extension === "gif") {
          ext = await extractor.extractImages(file.file, keys.current)
        }
      } catch (error) {
        console.log(error)
        file.error = error.toString()
        debugger
        return
      }


      const vectors = await createEmbeddings(ext.text, keys.current)

      // let locale = ext.locale || "en-US"

      for (const vector of vectors) {
        await supabase.from("vectors").insert({
          name: file.file.name,
          status: Statuses.Vectorized,
          // locale,
          content: vector.content,
          vector: vector.embedding as any,
          isChunk: false,
          size: file.file.size,
          team: team?.id,
          tokens: vector.tokens,
        })
      }

      if (ext.chunks && ext.chunks.length > 1) {
        for (const content of ext.chunks) {
          const vector = await documents.createEmbedding(content, keys.current)
          await supabase.from("vectors").insert({
            name: file.file.name,
            // locale,
            status: Statuses.Vectorized,
            content,
            vector: vector as any,
            isChunk: true,
            tokens: estimateTokenCount(content),
            team: team?.id,
          })
        }
      }

    } catch (error) {
      console.error("Error uploading file:", error)
      file.error = error.toString()
      debugger
    }

  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {

    if (!keys.current) {
      const apikeys = await app.fetchAPIkeys(team?.id)
      if (!apikeys || !apikeys.openai) {
        toast.error("OpenAI API key not found")
        return
      }
      keys.current = apikeys.openai
    }

    const files = event.target.files
    if (!files || files.length === 0) return

    const fileArray = Array.from(files)
    const progressArray: FileToAdd[] = fileArray.map(file => ({
      file,
      progress: 0,
      status: 'pending' as const
    }))

    setUploadProgress(progressArray)
    setCurrentFileIndex(0)
    setIsDialogOpen(true)
    uploadFiles(progressArray)
  }

  const uploadFiles = async (files: FileToAdd[]) => {


    const errors: { file: string; error: string }[] = []
    let successful = 0
    uploading.current = true

    try {
      for (let i = 0; i < files.length; i++) {
        const fileProgress = files[i]

        setCurrentFileIndex(i)
        setUploadProgress(prev => prev.map((f, idx) =>
          idx === i ? { ...f, status: 'uploading', progress: 0 } : f
        ))
        try {
          const progressInterval = setInterval(() => {
            setUploadProgress(prev => prev.map((f, idx) => idx === i && f.progress < 90 ? { ...f, progress: f.progress + 10 } : f))
          }, 200)
          const toup: FileToAdd = {
            file: fileProgress.file,
            name: fileProgress.file.name
          }
          await onFileUpload(toup)
          clearInterval(progressInterval)

          if (toup.error) {
            errors.push({ file: fileProgress.file.name, error: toup.error })
            setUploadProgress(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'error', progress: 0, error: toup.error } : f))
          } else {
            successful++
            setUploadProgress(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'completed', progress: 100, url: toup.url } : f))
          }
        } catch (error) {
          const errorMsg = error?.toString() || 'Unknown error'
          errors.push({ file: fileProgress.file.name, error: errorMsg })
          setUploadProgress(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'error', progress: 0, error: errorMsg } : f))
        }
      }
    } catch (err) {
      console.log("Error during upload: ", err)

    } finally {
      uploading.current = false
    }

    ctrl.reload()
    setIsDialogOpen(false)
    setUploadProgress([])
    if (fileInputRef.current) fileInputRef.current.value = ''

    setUploadSummary({ successful, errors })
    setShowSummary(true)
  }

  const handleCancel = () => {
    setIsDialogOpen(false)
    setUploadProgress([])
    setCurrentFileIndex(0)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function onShowChunksChange() {
    setShowChunks(!showChunks)
  }

  async function handleCreateDocument() {
    if (!newDocName.trim() || !newDocContent.trim()) {
      toast.error("Name and content are required")
      return
    }

    setIsCreatingDoc(true)

    try {
      if (!keys.current) {
        const apikeys = await app.fetchAPIkeys(team?.id)
        if (!apikeys || !apikeys.openai) {
          toast.error("OpenAI API key not found")
          setIsCreatingDoc(false)
          return
        }
        keys.current = apikeys.openai
      }

      const vectors = await createEmbeddings(newDocContent, keys.current)

      for (const vector of vectors) {
        await supabase.from("vectors").insert({
          name: newDocName.trim(),
          status: Statuses.Vectorized,
          locale: "en-US",
          content: vector.content,
          vector: vector.embedding as any,
          isChunk: false,
          team: team?.id,
          tokens: vector.tokens,
        })
      }

      const chunks = await chunkText(newDocContent, true)
      if (chunks && chunks.length > 1) {
        for (const chunkContent of chunks) {
          const chunkVector = await documents.createEmbedding(chunkContent, keys.current)
          await supabase.from("vectors").insert({
            name: newDocName.trim(),
            status: Statuses.Vectorized,
            locale: "en-US",
            content: chunkContent,
            vector: chunkVector as any,
            isChunk: true,
            team: team?.id,
            tokens: estimateTokenCount(chunkContent),
          })
        }
      }

      toast.success("Document created successfully")
      setIsNewDocDialogOpen(false)
      setNewDocName("")
      setNewDocContent("")
      ctrl.reload()
    } catch (error) {
      console.error("Error creating document:", error)
      toast.error("Failed to create document")
    } finally {
      setIsCreatingDoc(false)
    }
  }

  async function onAssignDomain(domainId: number | null) {
    if (selectedItems.length === 0) return
    ctrl.setLoading(true)
    const ids = selectedItems.map(item => item.id)
    const { error } = await supabase.from("vectors").update({ reference: domainId }).in("id", ids)
    if (error) {
      toast.error("Failed to assign domain")
      console.error(error)
    } else {
      toast.success(domainId ? "Domain assigned successfully" : "Domains removed successfully")
    }
    ctrl.setLoading(false)
    ctrl.reload()
  }

  async function onBulkAssignDomain() {
    const { domainId } = bulkDomainConfirm
    ctrl.setLoading(true)
    setBulkDomainConfirm({ open: false, domainId: null, domainName: "" })
    const { error } = await supabase.from("vectors").update({ reference: domainId }).eq("team", team?.id)
    if (error) {
      toast.error("Failed to assign domain to all items")
      console.error(error)
    } else {
      toast.success(domainId ? "Domain assigned to all items successfully" : "Domains removed from all items successfully")
    }
    ctrl.setLoading(false)
    ctrl.reload()
  }



  return <Page>
    <div className="h-full grid grid-rows-[auto_1fr] gap-4">
      <div className="flex">
        <PageTitle />
        <div className="grow"></div>
        <div className="flex gap-2">
          <input ref={fileInputRef} type="file" accept={acceptedFileTypes} multiple onChange={handleFileSelect} className="hidden" />
          <Button variant="outline" className="rounded-lg" onClick={() => setIsNewDocDialogOpen(true)}>
            <FileText className="h-4 w-4" />New Document
          </Button>
          <Button className="rounded-lg" onClick={() => fileInputRef.current?.click()}>
            <Plus className="h-4 w-4" />Upload Files
          </Button>
        </div>
      </div>

      <SearchBar onSearch={handleSearch} onClear={handleClear} />

      <div className="h-full min-h-0 flex">
        <DataTableSupabase
          table="vectors"
          columns={columns}
          // onRowClick={onRowClick}
          onRowClick={"/docu/supa"}
          onSelectedItemsChange={onItemSelectionChange}
          showSelection
          showSearch={false}
          filter={filter}
          onExternalLoad={onExternalLoad}
          disabled={uploading.current}
          select={`
          id,
          created_at,
          name,
          isChunk,
          locale,
          url,
          content,
          tokens,
          team (
            name
          ),
          reference (
            name
          )
        `}
          controller={ctrl}
          sort={["created_at", "desc"]}
          enableServerSideFiltering={true}
          searchableColumns={["name", "content"]}
        >
          {selectedItems.length > 0 &&
            <ToolbarItem>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Trash2 size={16} className="cursor-pointer hover:text-red-500" />
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete the documents.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onDeleteSelected} >Continue</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </ToolbarItem>}

          {selectedItems.length > 0 && !domainsLoading &&
            <ToolbarItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Globe size={16} className="cursor-pointer hover:text-blue-500" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => onAssignDomain(null)}>[Remove Domains]</DropdownMenuItem>
                  {domains?.map((domain: any) => (
                    <DropdownMenuItem key={domain.id} onClick={() => onAssignDomain(domain.id)}>{domain.name}</DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </ToolbarItem>}

          {!domainsLoading &&
            <ToolbarItem>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <GlobeLock size={16} className="cursor-pointer hover:text-blue-500" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        <DropdownMenuItem onClick={() => setBulkDomainConfirm({ open: true, domainId: null, domainName: "[Remove Domains]" })}>[Remove Domains]</DropdownMenuItem>
                        {domains?.map((domain: any) => (
                          <DropdownMenuItem key={domain.id} onClick={() => setBulkDomainConfirm({ open: true, domainId: domain.id, domainName: domain.name })}>{domain.name}</DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Assign Domain to All</p>
                </TooltipContent>
              </Tooltip>
            </ToolbarItem>}

          <ToolbarItem>
            <Tooltip>
              <TooltipTrigger asChild>
                <Puzzle size={16} className={cn("cursor-pointer", showChunks ? "fill-blue-400" : "stroke-slate-400")} onClick={onShowChunksChange} />
              </TooltipTrigger>
              <TooltipContent>
                <p>{showChunks ? "Hide Chunks" : "Show Chunks"}</p>
              </TooltipContent>
            </Tooltip>
          </ToolbarItem>

        </DataTableSupabase>


        {preview && <div className="w-3/4 mb-8 flex flex-col h-auto ml-4 border pr-2 pt-2" >
          <div className="w-full justify-end flex pb-1"><X className="h-4 w-4 cursor-pointer text-slate-400" onClick={() => setPreview(null)} /></div>
          <div className="font-bold pl-4 mb-2">{preview.name}</div>
          <div className="grow overflow-y-auto pl-4">
            {preview.content && <ToMarkdown>
              {preview.content}
            </ToMarkdown>}
            {!preview.content && <div className="w-full text-center pt-10 text-gray-400">
              No Content
            </div>}
          </div>
        </div>}

      </div>
    </div>

    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Upload Documents</DialogTitle>
          <DialogDescription>
            {`Uploading file ${currentFileIndex + 1} of ${uploadProgress.length}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {uploadProgress[currentFileIndex] && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium truncate flex-1">{uploadProgress[currentFileIndex].file.name}</span>
                <span className="text-muted-foreground ml-2">
                  {uploadProgress[currentFileIndex].status === 'completed' ? '100%' :
                    uploadProgress[currentFileIndex].status === 'uploading' ? `${uploadProgress[currentFileIndex].progress}%` :
                      uploadProgress[currentFileIndex].status === 'error' ? 'Error' : 'Pending'}
                </span>
              </div>
              <Progress value={uploadProgress[currentFileIndex].progress} className="h-2" />
              {uploadProgress[currentFileIndex].error && (
                <div className="text-sm text-red-500 mt-1">{uploadProgress[currentFileIndex].error}</div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={showSummary} onOpenChange={setShowSummary}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Upload Summary</DialogTitle>
          <DialogDescription>
            Upload process completed
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex justify-between text-sm">
            <span className="font-medium">Successful uploads:</span>
            <span className="text-green-600">{uploadSummary.successful}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="font-medium">Failed uploads:</span>
            <span className="text-red-600">{uploadSummary.errors.length}</span>
          </div>

          {uploadSummary.errors.length > 0 && (
            <div className="space-y-2">
              <div className="font-medium text-sm">Errors:</div>
              <ScrollArea className="h-[200px] w-full rounded-md border p-4">
                <div className="space-y-3">
                  {uploadSummary.errors.map((error, index) => (
                    <div key={index} className="space-y-1">
                      <div className="font-medium text-sm">{error.file}</div>
                      <div className="text-sm text-red-500">{error.error}</div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={() => setShowSummary(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <AlertDialog open={bulkDomainConfirm.open} onOpenChange={(open) => setBulkDomainConfirm({ ...bulkDomainConfirm, open })}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm Bulk Domain Assignment</AlertDialogTitle>
          <AlertDialogDescription>
            {bulkDomainConfirm.domainId === null
              ? "This will remove domains from ALL documents in the table. Are you sure you want to continue?"
              : `This will assign the domain "${bulkDomainConfirm.domainName}" to ALL documents in the table. Are you sure you want to continue?`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onBulkAssignDomain}>Continue</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <Dialog open={isNewDocDialogOpen} onOpenChange={setIsNewDocDialogOpen}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>New Document</DialogTitle>
          <DialogDescription>
            Create a new document with custom content
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="doc-name">Name</Label>
            <Input
              id="doc-name"
              placeholder="Document name"
              value={newDocName}
              onChange={(e) => setNewDocName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="doc-content">Content</Label>
            <Textarea
              id="doc-content"
              placeholder="Enter document content..."
              value={newDocContent}
              onChange={(e) => setNewDocContent(e.target.value)}
              rows={10}
              className="font-mono text-sm"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsNewDocDialogOpen(false)} disabled={isCreatingDoc}>
            Cancel
          </Button>
          <Button onClick={handleCreateDocument} disabled={isCreatingDoc}>
            {isCreatingDoc ? "Creating..." : "Create Document"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </Page >

}

interface SearchBarProps {
  onSearch: (params: { query: string; similarity: boolean }) => void
  onClear: () => void
}

function SearchBar({ onSearch, onClear }: SearchBarProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [similarity, setSimilarity] = useState(false)

  function handleSearch() {
    onSearch({ query: searchQuery, similarity })
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value
    setSearchQuery(value)
    if (value === "") {
      onClear()
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      handleSearch()
    }
  }

  return (
    <div className="flex items-center space-x-2 w-full">
      <div className="grow">
        <Input placeholder="Enter your question..." className="h-10 w-full" value={searchQuery} onChange={handleInputChange} onKeyDown={handleKeyDown} />
      </div>
      <div className="flex items-center space-x-2">
        <Checkbox id="similarity" checked={similarity} onCheckedChange={(checked) => setSimilarity(checked === true)} />
        <label htmlFor="similarity" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">Semantic</label>
      </div>
      <Button variant="outline" onClick={handleSearch}>Search</Button>
    </div>
  )
}
